import React, { useEffect, useState } from 'react';
import logo from '../images/haqdarshak-logo.png';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getSession } from "../utils/session";
import { VIEW_IDS } from "../constants/ViewIds";

const KPIHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const reduxViewIds = useSelector((state) => state.user.viewIds);
  const [viewIds, setViewIds] = useState(reduxViewIds || []);

  useEffect(() => {
    if (reduxViewIds?.length) {
      setViewIds(reduxViewIds);
    } else {
      const session = getSession();
      if (session?.viewIds?.length) setViewIds(session.viewIds);
    }
  }, [reduxViewIds]);

  const canView = (viewId) => viewIds.includes(viewId);

  const handleLogout = () => { 
    sessionStorage.clear(); 
    navigate("/"); 
  };

  const highlightKPI =
    location.pathname === "/Project-Level" ||
    location.pathname === "/Project-KPI-Comparison" ||
    location.pathname === "/Project-Scheme-Diversity";

  const highlightMilestone = location.pathname === "/Project-Milestone-Summary";

  const navItems = [
    { path: "/Project-Summary", label: "Project Summary", show: true },
    { path: "/Project-Level", label: "Project KPI", show: (canView(VIEW_IDS.PROJECT_LEVEL) || canView(VIEW_IDS.SCHEME_DIVERSITY) || canView(VIEW_IDS.COMPARISON)), isKPI: true },
    { path: "/Project-Milestone-Summary", label: "Project Milestone", show: canView(VIEW_IDS.PROJECT_MILESTONE) }
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 w-full bg-white shadow-lg z-50">
        <div className="px-4 sm:px-6 py-3">
          <div className="flex items-center">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-3">
              <img src={logo} alt="Logo" className="h-10 w-auto object-contain" />
              <div className="hidden sm:block h-8 w-px bg-gray-300"></div>
              <h1 className="hidden sm:block text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                PROJECT360 DASHBOARD
              </h1>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1 ml-[15vw]">
              {navItems.map((item) => (
                item.show && (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      location.pathname === item.path || (item.isKPI && highlightKPI)
                        ? "bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-md"
                        : "text-gray-700 hover:bg-gray-100 hover:text-pink-600"
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              ))}
            </div>

            {/* Right Section */}
            <div className="flex items-center ml-[22vw] space-x-3">
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="hidden sm:flex items-center space-x-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Logout</span>
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Brand Text */}
          <div className="sm:hidden mt-2">
            <h1 className="text-lg font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              PROJECT360 Dashboard
            </h1>
          </div>
        </div>

        {/* Bottom Gradient Border */}
        <div className="h-0.5 bg-gradient-to-r from-blue-500 via-pink-500 to-purple-500"></div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="fixed top-16 left-0 w-full bg-white shadow-xl z-40 md:hidden animate-slideDown">
          <div className="p-4 space-y-2">
            {navItems.map((item) => (
              item.show && (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === item.path || (item.isKPI && highlightKPI)
                      ? "bg-gradient-to-r from-pink-500 to-pink-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {item.label}
                </Link>
              )
            ))}
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                handleLogout();
              }}
              className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-3 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 mt-4"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default KPIHeader;